"use client"

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { Filter } from 'lucide-react'

type Post = any

function labelTag(t: string) {
  return String(t || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

export default function BlogFilterClient({
  posts,
  categories,
  tags,
  initialCategories = [],
  initialTags = [],
  initialQuery = '',
}: {
  posts: Post[]
  categories: string[]
  tags: string[]
  initialCategories?: string[]
  initialTags?: string[]
  initialQuery?: string
}) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories || [])
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags || [])
  const [query, setQuery] = useState(initialQuery || '')
  const [panelOpen, setPanelOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [mobileBarOpen, setMobileBarOpen] = useState(false)
  const [draftCategories, setDraftCategories] = useState<string[]>(selectedCategories)
  const [draftTags, setDraftTags] = useState<string[]>(selectedTags)
  const [mounted, setMounted] = useState(false)
  const desktopInputRef = useRef<HTMLInputElement | null>(null)
  const mobileInputRef = useRef<HTMLInputElement | null>(null)
  const mobileBarRef = useRef<HTMLDivElement | null>(null)
  const [desktopSearchActive, setDesktopSearchActive] = useState(false)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (!modalOpen) return
    setDraftCategories(selectedCategories)
    setDraftTags(selectedTags)
  }, [modalOpen, selectedCategories, selectedTags])

  useEffect(() => {
    if (!mounted) return
    const t = window.setTimeout(() => {
      const params = new URLSearchParams()
      if (selectedCategories.length) params.set('category', selectedCategories.slice().sort().join(','))
      if (selectedTags.length) params.set('tag', selectedTags.slice().sort().join(','))
      if (query) params.set('q', query)
      const qs = params.toString()
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
    }, 250)
    return () => window.clearTimeout(t)
  }, [selectedCategories, selectedTags, query, pathname, router, mounted])

  useEffect(() => {
    if (!modalOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [modalOpen])

  useEffect(() => {
    function onToggle() {
      const isMobile = window.innerWidth < 640
      if (isMobile) {
        setMobileBarOpen((prev) => {
          const next = !prev
          if (next) setTimeout(() => mobileInputRef.current?.focus(), 0)
          return next
        })
      } else {
        setDesktopSearchActive(true)
        setTimeout(() => desktopInputRef.current?.focus(), 0)
      }
    }
    window.addEventListener('open-blog-search', onToggle as EventListener)
    return () => window.removeEventListener('open-blog-search', onToggle as EventListener)
  }, [])

  useEffect(() => {
    if (!mobileBarOpen) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target as HTMLElement | null
      if (!target) return
      if (mobileBarRef.current?.contains(target)) return
      if (target.closest?.('[data-blog-search-trigger]')) return
      setMobileBarOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [mobileBarOpen])

  useEffect(() => {
    if (!mounted) return
    const active = mobileBarOpen || desktopSearchActive
    window.dispatchEvent(new CustomEvent('blog-search-state', { detail: { active } }))
  }, [mobileBarOpen, desktopSearchActive, mounted])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const tagSet = new Set(selectedTags.map((t) => t.toLowerCase()))
    const categorySet = new Set(selectedCategories.map((c) => c.toLowerCase()))
    return posts.filter((post: any) => {
      if (categorySet.size && !categorySet.has(String(post?.category || '').toLowerCase())) return false
      if (tagSet.size && !((post?.tags || []) as string[]).some((t) => tagSet.has(String(t).toLowerCase()))) return false
      if (!q) return true
      const hay = [
        post?.title,
        post?.category,
        ...(post?.tags || []),
        post?.seo?.metaDescription,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(q)
    })
  }, [posts, selectedCategories, selectedTags, query])

  function applyFilters() {
    setSelectedCategories(draftCategories)
    setSelectedTags(draftTags)
    setModalOpen(false)
  }

  function clearFilters() {
    setDraftCategories([])
    setDraftTags([])
  }

  function clearAll() {
    setSelectedCategories([])
    setSelectedTags([])
    setQuery('')
  }

  function toggleSelected(list: string[], value: string) {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  }

  function toggleDraft(list: string[], value: string) {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
  }

  return (
    <div className="space-y-4">
      <div className="hidden sm:flex items-center gap-2">
        <label className="flex-1">
          <span className="sr-only">Search posts</span>
          <input
            ref={desktopInputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setDesktopSearchActive(true)}
            onBlur={() => setDesktopSearchActive(false)}
            placeholder="Search blogs (care, styling, cleaning)"
            className={`w-full rounded border px-3 py-2 text-sm transition ${desktopSearchActive ? 'border-black ring-2 ring-black/10' : ''}`}
          />
        </label>
        <button
          type="button"
          onClick={() => setPanelOpen((prev) => !prev)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded border ${panelOpen ? 'bg-black text-white border-black' : 'hover:bg-gray-50'}`}
          aria-label="Toggle filters"
          aria-pressed={panelOpen}
        >
          <Filter size={18} />
        </button>
      </div>

      {panelOpen ? (
        <section className="hidden sm:block border bg-white p-4 shadow-sm space-y-4">
          {(selectedCategories.length || selectedTags.length || query) ? (
            <div className="flex items-center justify-end">
              <button type="button" onClick={clearAll} className="text-xs underline decoration-dotted">
                Clear
              </button>
            </div>
          ) : null}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Categories</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <button
                type="button"
                onClick={() => setSelectedCategories([])}
                className={`inline-flex items-center rounded border px-3 py-1.5 ${selectedCategories.length ? 'hover:bg-gray-50' : 'bg-black text-white border-black'}`}
              >
                All
              </button>
              {categories.map((c) => {
                const selected = selectedCategories.includes(c)
                return (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedCategories((prev) => toggleSelected(prev, c))}
                    className={`inline-flex items-center rounded border px-3 py-1.5 ${selected ? 'bg-black text-white border-black' : 'hover:bg-gray-50'}`}
                  >
                    {c}
                  </button>
                )
              })}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">Tags</div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <button
                type="button"
                onClick={() => setSelectedTags([])}
                className={`inline-flex items-center rounded border px-3 py-1.5 ${selectedTags.length ? 'hover:bg-gray-50' : 'bg-black text-white border-black'}`}
              >
                All
              </button>
              {tags.map((t) => {
                const selected = selectedTags.includes(t)
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedTags((prev) => toggleSelected(prev, t))}
                    className={`inline-flex items-center rounded border px-3 py-1.5 ${selected ? 'bg-black text-white border-black' : 'hover:bg-gray-50'}`}
                  >
                    {labelTag(t)}
                  </button>
                )
              })}
            </div>
          </div>
        </section>
      ) : null}

      {filtered.length ? (
        filtered.map((post: any) => (
          <Link key={post.slug} href={`/blog/${post.slug}/`} className="block" aria-label={post.title}>
            <article className="border bg-white p-5 shadow-sm space-y-3 hover:shadow-md transition">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                {post.readingTimeMinutes ? <span>{post.readingTimeMinutes} min read</span> : null}
              </div>
              {post.category ? <div className="text-[11px] uppercase tracking-wide text-gray-500">{post.category}</div> : null}
              <h2 className="text-lg font-semibold hover:underline">{post.title}</h2>
              {post?.seo?.metaDescription ? <p className="text-sm text-gray-600">{post.seo.metaDescription}</p> : null}
              {post?.hero?.image ? (
                <div className="relative overflow-hidden">
                  <img src={post.hero.image} alt={post.hero.alt || post.title} className="w-full h-auto object-contain" loading="lazy" decoding="async" width="960" height="540" />
                </div>
              ) : null}
            </article>
          </Link>
        ))
      ) : (
        <div className="border bg-white p-5 text-sm text-gray-600 shadow-sm">
          {(selectedCategories.length || selectedTags.length || query) ? 'No posts match your filters yet.' : 'New posts are coming soon.'}
        </div>
      )}

      {modalOpen && mounted ? (
        <div className="fixed inset-0 z-50 sm:hidden">
          <button type="button" aria-label="Close filters" className="fixed inset-0 bg-black/40" onClick={() => setModalOpen(false)} />
          <div className="absolute inset-0 w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 bg-white shadow-none overflow-hidden flex flex-col sm:relative sm:mx-auto sm:mt-16 sm:w-[92vw] sm:max-w-2xl sm:max-h-[85vh] sm:rounded sm:border sm:shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4 flex-shrink-0">
              <div className="text-lg font-semibold">Filters</div>
              <button type="button" onClick={() => setModalOpen(false)} className="inline-flex h-9 w-9 items-center justify-center rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Categories</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setDraftCategories([])}
                    className={`inline-flex items-center rounded border px-3 py-1.5 ${draftCategories.length ? 'hover:bg-gray-50' : 'bg-black text-white border-black'}`}
                  >
                    All
                  </button>
                  {categories.map((c) => {
                    const selected = draftCategories.includes(c)
                    return (
                      <button
                        key={c}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setDraftCategories((prev) => toggleDraft(prev, c))}
                        className={`inline-flex items-center rounded border px-3 py-1.5 ${selected ? 'bg-black text-white border-black' : 'hover:bg-gray-50'}`}
                      >
                        {c}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Tags</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setDraftTags([])}
                    className={`inline-flex items-center rounded border px-3 py-1.5 ${draftTags.length ? 'hover:bg-gray-50' : 'bg-black text-white border-black'}`}
                  >
                    All
                  </button>
                  {tags.map((t) => {
                    const selected = draftTags.includes(t)
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setDraftTags((prev) => toggleDraft(prev, t))}
                        className={`inline-flex items-center rounded border px-3 py-1.5 ${selected ? 'bg-black text-white border-black' : 'hover:bg-gray-50'}`}
                      >
                        {labelTag(t)}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0 border-t px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white">
              <div className="flex items-center justify-between gap-3">
                <button type="button" onClick={clearFilters} className="text-sm underline decoration-dotted">
                  Clear filters
                </button>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setModalOpen(false)} className="inline-flex items-center justify-center rounded border px-4 py-2 text-sm hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="inline-flex items-center justify-center rounded border border-brand bg-white px-5 py-2 text-sm font-medium text-black hover:bg-brand hover:text-white"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {mobileBarOpen ? (
        <div className="fixed inset-x-0 bottom-0 z-50 sm:hidden">
          <div
            ref={mobileBarRef}
            className="mx-auto max-w-4xl border-t bg-white px-3 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-lg"
          >
            <div className="flex items-center gap-2">
              <label className="flex-1">
                <span className="sr-only">Search posts</span>
                <input
                  ref={mobileInputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search blogs (care, styling, cleaning)"
                  className="w-full rounded border px-3 py-2 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded border hover:bg-gray-50"
                aria-label="Open filters"
              >
                <Filter size={18} />
              </button>
              <button
                type="button"
                onClick={() => setMobileBarOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded border hover:bg-gray-50"
                aria-label="Close search"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
